import { Severity, TestExpectationUnit } from "node-library/dist/constants";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	expectationInputStateHasValidationErrors,
	FrontendExpectationInputState,
} from "node-library/dist/frontendInputStates";
import {
	FrontendCompanyTestExpectationField,
	FrontendCompanyTestResultField,
	FrontendCompanyWithAdditionalInfomation,
} from "node-library/dist/portal";
import { GreenButton } from "../button";
import { CustomDropdown, CustomTextInput, RangeInputGroup } from "../inputs";
import { useSnackbar } from "../toast/showToastMessage";
import { GreenTickIcon } from "../toast/successIcons";
import { ModalWithButtons } from "./modal";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	fieldsNeedingExpectations: FrontendCompanyTestResultField[];
	// selectedTestHierarchyNodeIds: Array<string>;

	expectationInputs: FrontendExpectationInputState;
	setExpectationInputs: (
		expectationInputs: FrontendExpectationInputState,
	) => void;

	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function EditInstanceExpectationModal(props: Props) {
	const { openSnackbar } = useSnackbar();

	const { t: translation } = useTranslation();

	const { company, fieldsNeedingExpectations } = props;
	const [expectationInputs, setExpectationInputs] =
		useState<FrontendExpectationInputState>({});

	const fieldsByPackageNameMap: Map<
		string,
		Array<FrontendCompanyTestResultField>
	> = useMemo(() => {
		const grouped = new Map<string, Array<FrontendCompanyTestResultField>>();

		for (const field of fieldsNeedingExpectations) {
			// Find the full package objects from company data based on the field's ID list
			const associatedPackages = company.hierarchyNodeMappingPackages.filter(
				(pkg) => field.nodeMappingPackageIds.includes(pkg.packageId),
			);

			for (const pkg of associatedPackages) {
				const pkgName = pkg.packageName;

				if (!grouped.has(pkgName)) {
					grouped.set(pkgName, [field]);
				} else {
					// Prevent adding the same field twice to the same package group
					const existingFields = grouped.get(pkgName)!;
					if (!existingFields.some((f) => f.fieldId === field.fieldId)) {
						existingFields.push(field);
					}
				}
			}
		}
		return grouped;
	}, [fieldsNeedingExpectations, company.hierarchyNodeMappingPackages]);

	const [currentTab, setCurrentTab] = useState<string>(() => {
		// Get the first packageName from the map
		const firstKey = fieldsByPackageNameMap.keys().next().value;
		return firstKey ?? ""; // default to empty string if map is empty
	});

	const isSaveDisabled: boolean = expectationInputStateHasValidationErrors(
		fieldsNeedingExpectations,
		expectationInputs,
	);

	useEffect(() => {
		if (props.open) {
			const firstKey = fieldsByPackageNameMap.keys().next().value;

			setCurrentTab(firstKey || "");
		}
	}, [props.open]);

	useEffect(() => {
		setExpectationInputs(props.expectationInputs);
	}, [props.expectationInputs]);

	function saveExpectations() {
		const cleanedInputs: FrontendExpectationInputState = {};

		for (const [fieldId, input] of Object.entries(expectationInputs)) {
			if (input.type === "value") {
				if (input.value.trim() !== "") {
					cleanedInputs[fieldId] = input;
				}
			} else if (input.type === "range") {
				if (
					String(input.min).trim() !== "" ||
					String(input.max).trim() !== ""
				) {
					cleanedInputs[fieldId] = input;
				}
			}
		}

		props.setExpectationInputs(cleanedInputs);

		props.setUnsavedChanges(true);
		openSnackbar(
			translation("ExpectationSaved"),
			Severity.SUCCESS,
			undefined,
			<GreenTickIcon />,
		);
		props.setOpen(false);
	}

	const chooseTestOptionDropdown: React.ReactNode = (
		<CustomDropdown
			id="test-option-dropdown"
			label={translation("SelectATest")}
			labelClassName="typography-body-01"
			// description={translation("SelectTurnaroundTimeDescription")}
			// labelDescriptionColor={SemanticColor.INFO_MEDIUM}
			options={Array.from(fieldsByPackageNameMap.entries()).map((entry) => {
				const pkgName: string = entry[0];

				return {
					value: pkgName,
					title: pkgName,
				};
			})}
			value={currentTab}
			setValue={setCurrentTab}
		></CustomDropdown>
	);

	const tabContent: Array<React.ReactNode> = (
		fieldsByPackageNameMap.get(currentTab) || []
	).map((field) => {
		const expectationField: FrontendCompanyTestExpectationField =
			field.expectationField!;
		const existingValue =
			expectationInputs[expectationField.expectationFieldId];

		const unitId: string =
			existingValue?.unitId || field.availableUnits[0]?.unitId || "";
		if (expectationField.expectationFieldType === "value") {
			const value = existingValue?.type === "value" ? existingValue.value : "";

			return (
				<CustomTextInput
					key={expectationField.expectationFieldId}
					id={`expectation-${expectationField.expectationFieldId}`}
					label={field.fieldName}
					value={value}
					setValue={(newValue) => {
						setExpectationInputs((prev) => ({
							...prev,
							[expectationField.expectationFieldId]: {
								type: "value",
								value: newValue,
								unitId: unitId,
							},
						}));
					}}
				/>
			);
		} else if (expectationField.expectationFieldType === "range") {
			const min = existingValue?.type === "range" ? existingValue.min : "";
			const max = existingValue?.type === "range" ? existingValue.max : "";

			return (
				<RangeInputGroup
					label={field.fieldName}
					minValue={min}
					setMinValue={(newMin) => {
						setExpectationInputs((prev) => ({
							...prev,
							[expectationField.expectationFieldId]: {
								type: "range",
								min: newMin,
								max: max,
								unitId: unitId,
							},
						}));
					}}
					maxValue={max}
					setMaxValue={(newMax) => {
						setExpectationInputs((prev) => ({
							...prev,
							[expectationField.expectationFieldId]: {
								type: "range",
								min: min,
								max: newMax,
								unitId: unitId,
							},
						}));
					}}
					unitId={unitId}
					setUnitId={(newUnit) => {
						setExpectationInputs((prev) => ({
							...prev,
							[expectationField.expectationFieldId]: {
								type: "range",
								min: min,
								max: max,
								unitId: newUnit,
							},
						}));
					}}
					unitOptions={field.availableUnits}
					translation={translation}
					mandatory={expectationField.mandatory}
				></RangeInputGroup>
			);
		} else {
			return null;
		}
	});

	const editExpectationModal: React.ReactNode = (
		<ModalWithButtons
			headerText={translation("AddTestExpectation")}
			headerDescription={translation("AddTestExpectationDescription")}
			open={props.open}
			setOpen={props.setOpen}
			modalWidth={550}
			buttons={[
				<GreenButton
					key="save-expectation-button"
					id="save-expectation-button"
					children={translation("Save")}
					onClick={saveExpectations}
					disabled={isSaveDisabled}
				></GreenButton>,
			]}
		>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "space-around",
					height: "400px",
				}}
			>
				{fieldsByPackageNameMap.size == 0 ? (
					<div className="typography-heading-03 center-text">
						{translation("NoTestToCustomizeText")}
					</div>
				) : (
					<>
						<div>{chooseTestOptionDropdown}</div>
						<div
							style={{ height: "100%", overflowY: "auto", marginTop: "10px" }}
						>
							{tabContent}
						</div>
					</>
				)}
			</div>
		</ModalWithButtons>
	);
	return <div>{editExpectationModal}</div>;
}
